import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import { BookingStatusBanner } from '@/components/services/booking-status-banner';
import { SubscriptionPausedBanner } from '@/components/services/subscription-paused-banner';
import { ServiceDetailsTab } from '@/components/services/service-details-tab';
import { ServiceEnrollmentsTab } from '@/components/services/service-enrollments-tab';
import { ServiceFormScreen } from '@/components/services/service-form-screen';
import { ServiceOccurrencesTab } from '@/components/services/service-ocorreences-tab';
import { ServicePhotosManager } from '@/components/services/service-photos-manager';
import { ServiceTabHeader, TabConfig, TabKey } from '@/components/services/service-tab-header';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { DetailHeader } from '@/components/ui/detail-header';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { joinFirstNames } from '@/components/team/member-avatar';
import { Switch } from '@/components/ui/switch';
import { useMembership } from '@/hooks/use-membership';
import {
  useServiceCatalog,
  useUnofferCatalogItem,
} from '@/hooks/use-service-catalog';
import { useDeleteService, useService } from '@/hooks/use-services';
import { useActingProviderStore } from '@/store/actingProviderStore';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';
import { canEditCatalogIdentity } from '@/utils/catalog-permissions';
import { formatCurrency } from '@/utils/format-currency';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';

const tabs: TabConfig[] = [
  { key: 'details', title: 'Detalhes' },
  { key: 'enrollments', title: 'Contratos' },
  { key: 'schedules', title: 'Agendamentos' },
];

export default function ServiceDetailScreen() {
  const { id, edit } = useLocalSearchParams<{ id: string | string[]; edit?: string }>();
  const serviceId = Array.isArray(id) ? id[0] : id;

  const { data: service, isLoading, error, refetch } = useService(serviceId);
  const deleteService = useDeleteService();
  const { addToast } = useToastStore();

  // Serviço da equipe que não é meu: a barbearia é dona do preço, da duração e
  // do texto. Aqui eu só olho e digo se faço — editar é de quem o criou.
  const { membership, isOwner } = useMembership();
  const isTeam = membership?.type === 'TEAM';
  const { data: catalog = [] } = useServiceCatalog({ enabled: isTeam });
  const acting = useActingProviderStore((s) => s.acting);
  const selfProviderId = useAuthStore((s) => s.user?.providerProfile?.id ?? null);
  const focusedProviderId = acting?.providerId ?? selfProviderId;
  const unoffer = useUnofferCatalogItem();

  const catalogItemId = service?.catalogItemId ?? null;
  const readOnly = useMemo(() => {
    if (!isTeam || !catalogItemId) return false;
    const item = catalog.find((entry) => entry.id === catalogItemId);
    if (!item) return !isOwner;
    return !canEditCatalogIdentity(item, { isOwner, providerId: focusedProviderId });
  }, [isTeam, catalogItemId, catalog, isOwner, focusedProviderId]);

  const handleStopOffering = async () => {
    if (!catalogItemId || !focusedProviderId) return;
    try {
      await unoffer.mutateAsync({ id: catalogItemId, providerId: focusedProviderId });
      addToast('Você não faz mais esse serviço.', 'success');
      router.replace('/(tabs)/(provider)/services');
    } catch (e) {
      addToast(getApiErrorMessage(e, 'Não foi possível mudar isso agora.'), 'error');
    }
  };

  const [activeTab, setActiveTab] = useState<TabKey>('details');
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  // Copiar para a equipe é do dono, e só faz sentido com mais alguém ativo.

  const handleTabChange = useCallback((tabKey: TabKey) => setActiveTab(tabKey), []);

  const handleBack = () => router.replace('/(tabs)/(provider)/services');

  // Editar é a MESMA tela de criar, carregada com o serviço. "Editar" só liga o
  // parâmetro; o early-return abaixo troca a tela.
  const handleEdit = () => router.setParams({ edit: '1' });

  const handleDuplicate = () => {
    if (!serviceId) return;
    router.push(`/(tabs)/(provider)/services/create?fromServiceId=${serviceId}`);
  };

  const handleConfirmDelete = async () => {
    if (!serviceId) return;
    try {
      await deleteService.mutateAsync(serviceId);
      setDeleteConfirmModalOpen(false);
      addToast('Serviço excluído.', 'success');
      router.replace('/(tabs)/(provider)/services');
    } catch (e) {
      addToast(getApiErrorMessage(e, 'Erro ao excluir serviço.'), 'error');
    }
  };

  const enrollments = useMemo(() => service?.enrollments, [service?.enrollments]);

  // `?edit=1` só existe para quem manda no serviço: um link velho (ou o botão
  // de outra tela) não vira formulário de um serviço da casa.
  if (edit === '1' && serviceId && !readOnly) {
    return <ServiceFormScreen serviceId={serviceId} />;
  }

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas">
        <ActivityIndicator size="large" className="text-primary" />
        <ThemedText className="mt-2 text-foreground">Carregando serviço...</ThemedText>
      </View>
    );
  }

  if (error || !service) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas p-5">
        <Ionicons name="alert-circle-outline" size={48} className="mb-4 text-error-600" />
        <ThemedText className="mb-4 text-center text-foreground">
          Não foi possível carregar os detalhes do serviço.
        </ThemedText>
        <Button title="Tentar novamente" onPress={() => refetch()} variant="outline" />
        <Button title="Voltar" onPress={handleBack} variant="link" className="mt-4" />
      </View>
    );
  }

  return (
    <View className="w-full flex-1 bg-canvas">
      <Dialog open={deleteConfirmModalOpen} onOpenChange={setDeleteConfirmModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir serviço</DialogTitle>
            <DialogDescription>
              Tem certeza? Isso removerá o serviço e todos os contratos, cobranças e agendamentos
              vinculados. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex-row gap-3">
            <Button
              title="Cancelar"
              variant="outline"
              onPress={() => setDeleteConfirmModalOpen(false)}
              className="flex-1"
            />
            <Button
              title="Excluir"
              variant="destructive"
              onPress={handleConfirmDelete}
              disabled={deleteService.isPending}
              className="flex-1"
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DashboardScrollView
        key={activeTab}
        className="w-full flex-1 bg-canvas"
        contentContainerStyle={{ paddingBottom: 80 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View className="w-full gap-4 pt-5">
          <DetailHeader
            title={service.name}
            subtitle={
              service.defaultPrice != null
                ? formatCurrency(service.defaultPrice)
                : 'Preço combinado por contrato'
            }
            onBack={handleBack}
            actions={
              readOnly ? undefined : <Button title="Editar" size="sm" onPress={handleEdit} />
            }
          />

          <ServiceTabHeader
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            isEditing={false}
          />

          {activeTab === 'details' && (
            <>
              <View className="gap-2.5 px-5">
                <BookingStatusBanner status={service.bookingStatus} />
                <SubscriptionPausedBanner service={service} />
                {service.teamCopies?.length ? (
                  <View className="flex-row items-center gap-1.5 self-start rounded-full bg-neutral-100 px-3 py-1">
                    <Ionicons className="text-neutral-600" name="people-outline" size={13} />
                    <ThemedText className="font-semibold text-xs text-neutral-600">
                      Também com {joinFirstNames(service.teamCopies.map((c) => c.name))}
                    </ThemedText>
                  </View>
                ) : null}
              </View>

              {readOnly ? null : <ServicePhotosManager serviceId={serviceId} />}
              <ServiceDetailsTab service={service} />
              {readOnly ? (
                <View className="px-5 pt-2">
                  <View className="gap-3 rounded-2xl border border-neutral-200 bg-card p-4">
                    <ThemedText className="text-xs text-neutral-500">
                      Este serviço é da equipe: o preço, a duração e o texto são de quem o criou.
                    </ThemedText>
                    <View className="flex-row items-center gap-3">
                      <View className="flex-1 gap-0.5">
                        <ThemedText className="font-semibold text-foreground">
                          Eu também faço
                        </ThemedText>
                        <ThemedText className="text-xs text-neutral-500">
                          Desligado, ele sai da sua lista e da sua agenda.
                        </ThemedText>
                      </View>
                      <Switch
                        checked
                        disabled={unoffer.isPending}
                        onCheckedChange={() => void handleStopOffering()}
                      />
                    </View>
                  </View>
                </View>
              ) : (
                <View className="gap-2.5 px-5 pt-2">
                  <Button
                    title="Duplicar serviço"
                    variant="outline"
                    onPress={handleDuplicate}
                    className="w-full"
                  />
                  <Button
                    variant="outline"
                    onPress={() => setDeleteConfirmModalOpen(true)}
                    disabled={deleteService.isPending}
                    className="w-full border-error-500/30"
                  >
                    <ThemedText className="font-semibold text-sm text-error-600">
                      Excluir serviço
                    </ThemedText>
                  </Button>
                </View>
              )}
            </>
          )}

          {activeTab === 'enrollments' && (
            <ServiceEnrollmentsTab enrollments={enrollments} serviceId={serviceId} />
          )}
          {activeTab === 'schedules' && <ServiceOccurrencesTab serviceId={serviceId} />}
        </View>
      </DashboardScrollView>
    </View>
  );
}
